import propTypes from 'prop-types';
import classnames from 'classnames'
import { withStyles } from '@material-ui/core';
import styles from './styles';
import { getSheetEventTitle, getStatusColor, getStatusLetter } from 'pages/ScheduleV6/utils';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import ArrowRightBlack from 'assets/images/arrow-right-black.svg';
import { useCallback, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import WorklogDetailsCard from 'pages/ScheduleV6/components/card/WorklogDetailsCard';
import { useHandleDrop } from 'pages/ScheduleV6/components/events/hooks/useHandleResourceDrop';

const WeeklyEventCard = ({
  classes,
  event,
  handleClick,
  noClSheets,
}) => {
  const popperRef = useRef(null);
  const cardRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const sheet = useMemo(() => event?.sheet, [event?.sheet])

  const { handleDrop } = useHandleDrop({
    sheet: sheet,
  });

  const getStatusIconComponent = () => {
    const letter = getStatusLetter(sheet);
    const color = getStatusColor(sheet);
    return (
      <div
        className={classes.statusItemIconWrapper}
        style={{ borderColor: color, color: color }}
      >
        {letter}
      </div>
    );
  };
  
  const timeRange = useMemo(() => {
    let timeRange = '';
    if (sheet.hours?.start && sheet.hours?.end) {
      try {
        const start = new Date(sheet.hours?.start);
        const end = new Date(sheet.hours?.end);
        timeRange = `${format(start, 'p')} - ${format(end, 'p')}`;
      } catch {
        timeRange = '';
      }
    }
    return timeRange;
  }, [sheet])

  const open = Boolean(anchorEl);

  const handleExpandClick = useCallback((event) => {
    event.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      const rect = cardRef.current.getBoundingClientRect();
      setAnchorEl({ getBoundingClientRect: () => rect });
    }
  }, [anchorEl, cardRef, setAnchorEl]);

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div styles={{overflow: 'visible'}}>
        <Card
          className={classnames(
            classes.weeklyEventCard,
            {
              [classes.nightShiftEventCard]: sheet.timeOfDay === 'Night',
              [classes.publishError]: (noClSheets || [])?.includes(sheet._id),
            }
          )}
          ref={cardRef}
          onClick={handleClick}
        >
          <div className={classes.expandIconWrapper}>
            <span className={classes.expandIconBase} onClick={handleExpandClick}>
              <img src={ArrowRightBlack} className={classes.iconImage} alt="arrow" />
            </span>
            {event?.locationColor ? (
              <div
                className={classes.locationMark}
                style={{ borderTopColor: event?.locationColor, borderRightColor: event?.locationColor }}
              />
            ) : null}
          </div>
          <div className={classes.jobNumberWrapper}>
            {getStatusIconComponent()}
            {sheet.project?.jobNumber ? (
              <span className={classes.jobNumber}>#{sheet.project.jobNumber}</span>
            ) : null}
          </div>
          <div className={classes.titleWrapper}>
            <Typography variant="body1" color="textPrimary" style={{ display: 'flex' }}>
              {getSheetEventTitle(sheet, 'short')}
            </Typography>
          </div>
          <div className={classes.contractorWrapper}>
            <Typography variant="body1" color="textSecondary" style={{ display: 'flex' }}>
              {sheet.project?.contractor?.name}
            </Typography>
          </div>
          {timeRange ? (
            <div className={classes.timeRangeWrapper}>
              <Typography variant="body1" color="textPrimary" style={{ display: 'flex' }}>
                {timeRange}
              </Typography>
            </div>
          ) : null}
        </Card>

        <Popper
          id={sheet._id}
          ref={popperRef}
          open={open}
          anchorEl={anchorEl}
          placement={'bottom-start'}
          className={classnames(classes.popper)}
          flip={false}
          popperRef={(node) => {
            popperRef.current = node;
          }}
          style={{ zIndex: 999 }}
          modifiers={{
            flip: {
              enabled: false,
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: 'viewport',
            },
          }}
        >
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <WorklogDetailsCard sheet={sheet} close={handleClose} />
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}

WeeklyEventCard.propTypes = {
  classes: propTypes.object,
  event: propTypes.object.isRequired,
  locationColor: propTypes.string,
  handleClick: propTypes.func.isRequired,
  noClSheets: propTypes.arrayOf(propTypes.string),
}

export default withStyles(styles)(WeeklyEventCard)